import { translations, supportedLanguages } from './translations.js';

/**
 * Detects the best language before login based on browser/OS settings.
 * Checks navigator.languages in order of preference and falls back to 'en'.
 * 
 * @returns {string} Supported 2-letter language code
 */
export function detectInitialLanguage() {
  // 1. Check if user previously picked a language manually in this session
  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      const saved = window.sessionStorage.getItem('mfa_user_language');
      if (saved && translations[saved]) {
        return saved;
      }
    } catch (_) {}
  }

  // 2. Check browser/OS languages
  if (typeof navigator !== 'undefined') {
    const candidateLanguages = navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language || navigator.userLanguage];

    for (const rawLang of candidateLanguages) {
      if (!rawLang || typeof rawLang !== 'string') continue;
      const code = rawLang.split('-')[0].toLowerCase();
      if (translations[code]) {
        return code;
      }
    }
  }

  // 3. Global corporate fallback
  return 'en';
}

/**
 * Maps a 2-letter ISO country code (usageLocation) or browser locale to one of our 9 supported languages.
 * Supported languages: de, en, da, hu, sv, lt, es, fr, fi.
 * 
 * @param {string} [usageLocation] - Entra ID usageLocation (e.g. 'DE', 'DK', 'HU', 'SE', 'LT', 'ES', 'FR', 'FI')
 * @param {string} [preferredLanguage] - Entra ID preferredLanguage (e.g. 'de-DE', 'da-DK')
 * @returns {string} Supported language code (default 'en' or browser fallback)
 */
export function detectLanguage(usageLocation, preferredLanguage) {
  // 1. Check Entra ID usageLocation (primary requirement)
  if (usageLocation && typeof usageLocation === 'string') {
    const loc = usageLocation.trim().toUpperCase();
    
    // Check direct location mappings
    for (const lang of supportedLanguages) {
      if (lang.locations && lang.locations.includes(loc)) {
        return lang.code;
      }
    }
  }

  // 2. Check preferredLanguage attribute from Graph API if present
  if (preferredLanguage && typeof preferredLanguage === 'string') {
    const prefCode = preferredLanguage.trim().toLowerCase().split('-')[0];
    if (translations[prefCode]) {
      return prefCode;
    }
  }

  // 3. Check browser/OS language or English fallback
  return detectInitialLanguage();
}

/**
 * Returns country display name for the 2-letter code if available
 */
export function getCountryDisplay(usageLocation) {
  if (!usageLocation) return null;
  const loc = usageLocation.toUpperCase();
  const countryNames = {
    DE: "Germany (Deutschland)",
    AT: "Austria (Österreich)",
    CH: "Switzerland (Schweiz)",
    US: "United States",
    GB: "United Kingdom",
    DK: "Denmark (Danmark)",
    HU: "Hungary (Magyarország)",
    SE: "Sweden (Sverige)",
    LT: "Lithuania (Lietuva)",
    ES: "Spain (España)",
    FR: "France",
    FI: "Finland (Suomi)"
  };
  return countryNames[loc] || loc;
}
